import { Button } from '@design/components/button/Button';
import { Drawer } from '@design/components/drawer/Drawer';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { DrawerContentWrapper } from '@terminal/components/drawer-content-wrapper/DrawerContentWrapper';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useSubscription } from '@terminal/stores/use-subscription/useSubscription';
import { ThemeOverviewContent } from '@terminal/widgets/theme-widget/theme-overview-content/ThemeOverviewContent';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styles from './ThemeOverviewDrawer.module.scss';

export interface ThemeOverviewDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  themeId: string | undefined;
  trackingSource?: PosthogTrackingSource;
  dataTestId?: string;
  className?: string;
}

export const ThemeOverviewDrawer = ({
  isOpen,
  onClose,
  themeId,
  trackingSource = POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW_DRAWER,
  dataTestId = 'theme-overview-drawer',
  className,
}: ThemeOverviewDrawerProps) => {
  const { t } = useTranslation(['widget']);
  const { navigateToTheme } = useNavigateToThemeOverview();
  const { data: theme } = useTheme({ themeId: isOpen ? themeId : undefined });

  const isDrawerOverhaulEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-5200-drawer-overhaul')
  );
  const isBasicUser = useSubscription(state => state.isBasicUser());
  const enabledDrawerOverhaul = isDrawerOverhaulEnabled && !isBasicUser;

  const handleExpand = () => {
    if (!theme) {
      return;
    }
    navigateToTheme({ themeId: theme.id, source: trackingSource });
  };

  return (
    <Drawer
      className={clsx(styles.drawer, className)}
      data-testid={dataTestId}
      isOpen={isOpen}
      handleCloseClick={onClose}
      headerButton={
        theme ? (
          <div>
            <WebOnlyFeat>
              {!enabledDrawerOverhaul && (
                <Button
                  variant="empty"
                  label={t('widget:themeOverview.drawer.themeOverview')}
                  iconName="Expand"
                  size="xsmall"
                  onClick={handleExpand}
                />
              )}
            </WebOnlyFeat>
          </div>
        ) : undefined
      }
    >
      <DrawerContentWrapper>
        <ErrorBoundaryWrapper resetKeys={[isOpen, theme?.id]}>
          <div className={styles.wrapper}>
            {theme ? (
              <ThemeOverviewContent
                theme={{ theme }}
                source={trackingSource}
                title={theme.name}
                tooltipThemeId={theme.id}
              />
            ) : (
              <SkeletonLoader
                areas={[
                  {
                    styles: {
                      width: '100%',
                      height: '20px',
                      borderRadius: '8px',
                    },
                  },
                ]}
              />
            )}
          </div>
        </ErrorBoundaryWrapper>
      </DrawerContentWrapper>
    </Drawer>
  );
};
